import { Injectable } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { Messaging, getMessaging, getToken, onMessage } from '@angular/fire/messaging';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  private messaging: Messaging;
  public notificationPermission = new BehaviorSubject<NotificationPermission>(
    isPlatformBrowser(this.platformId) && 'Notification' in window ? 
    Notification.permission : 
    'default'
  );

  constructor(
    private firebaseApp: FirebaseApp,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.messaging = getMessaging(this.firebaseApp);
    }
  }

  isSupported(): boolean {
    return isPlatformBrowser(this.platformId) && 
      typeof window !== 'undefined' && 
      'serviceWorker' in navigator && 
      'PushManager' in window &&
      // Additional Safari check
      !this.isSafariBlockingPush();
  }

  private isSafariBlockingPush(): boolean {
    // Safari 16.4+ supports the Push API but with limitations
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Safely get the version number
    const versionMatch = navigator.userAgent.match(/Version\/(\d+\.\d+)/);
    const version = versionMatch ? parseFloat(versionMatch[1]) : 0;
    
    return isSafari && version < 16.4;
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      alert('FCM not supported in this browser');
      console.error('Notifications not supported in this browser');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission()
        .catch(error => {
          console.warn('Permission request failed:', error);
          return Notification.permission; // Return current permission state
        });
      
      this.notificationPermission.next(permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Add this new method
  checkIfPermissionBlocked(): boolean {
    if (!this.isSupported()) return false;
    
    // In Chrome, when blocked, permission is 'denied' but not via Notification.permission
    // So we need to track our own state
    return localStorage.getItem('notifications_blocked') === 'true';
  }

  async getToken(): Promise<string | null> {
    // if (!this.isSupported() || !this.messaging) {
    //   return null;
    // }

    try {
      const serviceWorkerRegistration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js', 
        { scope: '/firebase-cloud-messaging-push-scope' }
      );

      return await getToken(this.messaging, {
        vapidKey: "BBCCp_Rmc6BTNovECVUFYlw86L6WgmJucuOPPxhwHn4iDIdlSpUwD_5mFq7KX0P0Zob39FlSlFsS_BxjOcBvT0k",
        serviceWorkerRegistration // Explicitly pass the SW registration
      });
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  generateRandomFCMToken(): string {
    // FCM tokens typically have two parts separated by a colon
    const part1Length = 22; // First part is usually 22 chars
    const part2Length = 152; // Second part is usually 152 chars
    
    // Generate random alphanumeric characters
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    
    // Generate first part (before colon)
    let part1 = '';
    for (let i = 0; i < part1Length; i++) {
      part1 += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Generate second part (after colon)
    let part2 = 'APA91b'; // Common FCM prefix
    for (let i = 0; i < part2Length - 6; i++) {
      part2 += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // console.log(`${part1}:${part2}`)
    localStorage.setItem('FCM_Token', `${part1}:${part2}`);
    return `${part1}:${part2}`;
  }

  // Remove the old getFcmToken() method as it's redundant now
}